// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// NGRX
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
// Auth
import { AuthNoticeService, AuthService, Register, User } from '../../../../core/auth/';
import { Subject } from 'rxjs';
import { ConfirmPasswordValidator } from './confirm-password.validator';

@Component({
    selector: 'kt-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class RegisterComponent implements OnInit, OnDestroy {
    registerForm: FormGroup;
    loading = false;
    errors: any = [];
    isButtonDisabled = false;

    private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

    /**
     * Component constructor
     *
     * @param authNoticeService: AuthNoticeService
     * @param translate: TranslateService
     * @param router: Router
     * @param auth: AuthService
     * @param store: Store<AppState>
     * @param fb: FormBuilder
     * @param cdr: ChangeDetectorRef
     */
    constructor(
        private authNoticeService: AuthNoticeService,
        private translate: TranslateService,
        private router: Router,
        private auth: AuthService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef
    ) {
        this.unsubscribe = new Subject();
    }

    ngOnInit() {
        this.authNoticeService.setNotice(
            '',
            'warning'
        );
        this.initRegisterForm();
        this.registerForm.controls.isForeign.valueChanges.subscribe((value) => {
            if (value) {
                this.registerForm.controls.cardSeriesAndNumber.disable();
            } else {
                this.registerForm.controls.cardSeriesAndNumber.enable();
            }
        });

        this.registerForm.controls.identificationNumber.valueChanges.subscribe((value) => {
            if (value.substring(0, 2) == '99') {

                this.registerForm.controls.cardSeriesAndNumber.disable();
                this.registerForm.controls.cardSeriesAndNumber.setValue('111111111');
                this.registerForm.controls.isForeign.setValue(true);
                this.registerForm.controls.cardSeriesAndNumber.setValidators([
                    Validators.minLength(9),
                    Validators.maxLength(9),
                ]);
                this.registerForm.controls.cardSeriesAndNumber.updateValueAndValidity();
            } else {
                this.registerForm.controls.isForeign.setValue(false);
                this.registerForm.controls.cardSeriesAndNumber.enable();
            }
        });

        this.registerForm.patchValue({
            firstName: '',
            lastName: '',
            motherName: '',
            identificationNumber: '',
            cardSeriesAndNumber: '',
            password: '',
            confirmPassword: '',
            email: '',
            isForeign: false,
            dateOfBirth: null,
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.loading = false;
    }

    initRegisterForm() {
        this.registerForm = this.fb.group(
            {
                firstName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(150)])],
                lastName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(150)])],
                motherName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(150)])],
                identificationNumber: [
                    '',
                    Validators.compose([Validators.required, Validators.minLength(11), Validators.maxLength(11)]),
                ],
                cardSeriesAndNumber: [
                    '',
                    Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
                ],
                email: [
                    '',
                    Validators.compose([
                        Validators.required,
                        Validators.email,
                        Validators.minLength(3),
                        // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
                        Validators.maxLength(320),
                    ]),
                ],
                password: ['', Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(100)])],
                confirmPassword: [
                    '',
                    Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(100)]),
                ],
                isForeign: [false, Validators.compose([Validators.required])],
                dateOfBirth: [null, Validators.compose([Validators.required])],
            },
            {
                validator: ConfirmPasswordValidator.MatchPassword,
            }
        );
    }

    /**
     * Form Submit
     */
    submit() {
        this.isButtonDisabled = true;
        setTimeout(() => {

            const controls = this.registerForm.controls;

            // check form
            if (this.registerForm.invalid) {
                Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
                return;
            }

            this.loading = true;

            // if (!controls.agree.value) {
            //     // you must agree the terms and condition
            //     // checkbox cannot work inside mat-form-field https://github.com/angular/material2/issues/7891
            //     this.authNoticeService.setNotice('You must agree the terms and condition', 'danger');
            //     return;
            // }

            // const user: User = new User();
            // user.clear();
            // user.email = controls.email.value;
            // user.username = controls.username.value;
            // user.fullname = controls.fullname.value;
            // user.password = controls.password.value;
            // user.roles = [];
            this.auth
                .register(this.registerForm.value)
                .pipe(
                    tap((userResponse) => {
                        if (userResponse) {
                            this.store.dispatch(new Register({ authToken: userResponse.accessToken }));
                            // pass notice message to the login page
                            this.authNoticeService.setNotice(this.translate.instant('AUTH.REGISTER.SUCCESS'), 'success');
                            this.router.navigateByUrl('/auth/login');
                        } else {
                            this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
                        }
                    }),
                    takeUntil(this.unsubscribe),
                    finalize(() => {
                        this.loading = false;
                        this.isButtonDisabled = false;
                        this.cdr.markForCheck();
                    })
                )
                .subscribe();

            
        }, 500);
    }

    /**
     * Checking control validation
     *
     * @param controlName: string => Equals to formControlName
     * @param validationType: string => Equals to valitors name
     */
    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.registerForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result = control.hasError(validationType) && (control.dirty || control.touched);
        return result;
    }
}
